import React from "react";

/**
 * Ophthalmic 360° cornea placement dial.
 * All axes are in degrees (0-180 for corneal meridian). The dial displays a
 * full 360° compass with tick marks every 10° and labels every 30°.
 */
export default function ToricDial({ steepAxis, iolAxis, incisionAxis = 180, accent = "sky" }) {
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 14;

  // Corneal-meridian angle -> SVG angle. Corneal 0° is nasal/temporal horizontal.
  // We map corneal angle θ to unit vector (cos, -sin) so that 90° points UP.
  const toXY = (angle, radius) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy - radius * Math.sin(rad) };
  };

  const ticks = [];
  for (let a = 0; a < 360; a += 10) {
    const outer = toXY(a, r);
    const inner = toXY(a, r - (a % 30 === 0 ? 10 : 5));
    ticks.push(<line key={a} x1={outer.x} y1={outer.y} x2={inner.x} y2={inner.y} stroke="#CBD5E1" strokeWidth={a % 30 === 0 ? 1.2 : 0.8} />);
  }

  const labels = [];
  for (let a = 0; a < 180; a += 30) {
    const p = toXY(a, r - 22);
    labels.push(
      <text key={"l" + a} x={p.x} y={p.y} textAnchor="middle" alignmentBaseline="central" fontSize="9" fill="#64748B" fontFamily="JetBrains Mono, monospace">
        {a}
      </text>
    );
  }

  const axisLine = (angle, color, width = 2, dash) => {
    // Corneal axis is a meridian: line goes through center in both directions.
    const a = toXY(angle, r - 4);
    const b = toXY(angle + 180, r - 4);
    return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={color} strokeWidth={width} strokeDasharray={dash} strokeLinecap="round" />;
  };

  const arrow = (angle, color) => {
    const p = toXY(angle, r - 4);
    return <circle cx={p.x} cy={p.y} r={4} fill={color} />;
  };

  const iolColor = accent === "emerald" ? "#059669" : "#0284C7";

  return (
    <div className="w-full flex justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="max-w-full">
        <circle cx={cx} cy={cy} r={r} fill="#F8FAFC" stroke="#E2E8F0" />
        {ticks}
        {labels}
        {/* Incision at 180° (temporal) */}
        {axisLine(incisionAxis, "#94A3B8", 1.5, "3 4")}
        <text x={cx - r + 2} y={cy - 6} fontSize="8" fill="#94A3B8" fontFamily="JetBrains Mono, monospace">incision 180°</text>
        {/* Steep axis */}
        {axisLine(steepAxis, "#DC2626", 2)}
        {arrow(steepAxis, "#DC2626")}
        {/* IOL alignment axis */}
        {axisLine(iolAxis, iolColor, 3)}
        {arrow(iolAxis, iolColor)}
        {/* Center */}
        <circle cx={cx} cy={cy} r={3} fill="#0F172A" />
        {/* Legend */}
      </svg>
    </div>
  );
}
